import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ListCard from "../components/ListCard";
import {
  GET_TRUST_CENTER_ACCESS_REQUESTS,
  GRANT_TRUST_CENTER_ACCESS,
  REVOKE_TRUST_CENTER_ACCESS,
} from "../../../../apis/trust-center-apis/trustCenterApis";
import {
  includesQuery,
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import RefreshButton from "../../components/RefreshButton";
import RequestAlertsCard from "../components/RequestAlertsCard";

const PAGE_SIZE = 200;

const STATE_LABELS = {
  pending: "In progress",
  approved: "Accepted",
  revoked: "Declined",
};

const inProgress = (row) => row.accessState === "pending";

// Actions has a fixed width that fits both buttons; the text columns share
// the rest and clip with a tooltip.
const progressColumns = [
  { label: "Name", field: "name", width: "13%" },
  { label: "Email", field: "email", width: "17%" },
  { label: "Company", field: "company", width: "11%" },
  { label: "Document", field: "resourceName", width: "15%" },
  { label: "Reason", field: "reason", width: "13%" },
  { label: "Requested at", field: "requestedAt", width: "12%", sortable: true },
  { label: "Actions", field: "actions", width: "210px", align: "right" },
];

const completedColumns = [
  { label: "Name", field: "name", width: "14%" },
  { label: "Email", field: "email", width: "18%" },
  { label: "Company", field: "company", width: "12%" },
  { label: "Document", field: "resourceName", width: "16%" },
  { label: "Reason", field: "reason", width: "16%" },
  { label: "Status", field: "accessState", width: "12%" },
  { label: "Requested at", field: "requestedAt", width: "12%", sortable: true },
];

const noWrap = { whiteSpace: "nowrap" };

// Same clip as People: the cell stays one line, and hover always has the
// full value. Measuring overflow first left long names as dots with no tip.
const ClippedText = ({ value, title }) => {
  const theme = useTheme();
  const label = value || "—";
  const tip = title === undefined ? value || "" : title;
  const text = (
    <Typography
      variant="body2"
      sx={{
        color: theme.customColors.text.strong,
        textAlign: "left",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  );
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      {tip ? (
        <Tooltip title={tip} arrow>
          {text}
        </Tooltip>
      ) : (
        text
      )}
    </Box>
  );
};

// Email and company hover with the reason the visitor gave.
const valueAndReason = (value, reason) => {
  if (!reason) return value || "";
  return (
    <Stack spacing={0.25} sx={{ py: 0.25, maxWidth: 320 }}>
      <span>{value}</span>
      <span>{reason}</span>
    </Stack>
  );
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

// `alertEmails` / `onSaveAlerts`: the admins emailed about new requests.
// Every document on a request (older requests have only resourceName).
const documentsOf = (row) =>
  row.resourceNames?.length
    ? row.resourceNames
    : row.resourceName
      ? [row.resourceName]
      : [];

const AccessRequestsTab = ({ alertEmails = [], onSaveAlerts }) => {
  const theme = useTheme();
  const { data, error, refetch } = useQuery(GET_TRUST_CENTER_ACCESS_REQUESTS, {
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: "cache-and-network",
  });
  const page = data?.getTrustCenterAccessRequests;
  const items = page?.items || [];

  const pendingCount = useMemo(
    () => items.filter((r) => r.accessState === "pending").length,
    [items]
  );

  const [search, setSearch] = useState("");
  const [queue, setQueue] = useState("progress");
  // Newest first. The shared header clears the sort on the click after
  // descending; that leaves this list in the same order, so flip instead.
  const [sortBy, setSortBy] = useState(["-requestedAt"]);
  const onSortChange = (next) => {
    setSortBy(next.length === 0 ? ["requestedAt"] : next);
  };

  const [grantAccess, { loading: granting }] = useMutation(
    GRANT_TRUST_CENTER_ACCESS,
    { onCompleted: () => refetch() }
  );
  const [revokeAccess, { loading: revoking }] = useMutation(
    REVOKE_TRUST_CENTER_ACCESS,
    { onCompleted: () => refetch() }
  );
  const [actionError, setActionError] = useState("");
  const [actionOk, setActionOk] = useState("");

  const rows = useMemo(() => {
    const filtered = items.filter(
      (row) =>
        (queue === "progress" ? inProgress(row) : !inProgress(row)) &&
        includesQuery(
          search,
          row.name,
          row.email,
          row.company,
          documentsOf(row).join(" "),
          row.reason
        )
    );
    const entry = sortBy[0];
    if (entry !== "requestedAt" && entry !== "-requestedAt") return filtered;
    const desc = entry.startsWith("-");
    return [...filtered].sort((a, b) => {
      const av = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
      const bv = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
      return desc ? bv - av : av - bv;
    });
  }, [items, search, queue, sortBy]);

  const busy = granting || revoking;

  const onGrant = async (id) => {
    setActionError("");
    setActionOk("");
    try {
      await grantAccess({ variables: { id } });
      setActionOk("Request accepted. The document was emailed to them.");
    } catch (err) {
      setActionError(err.message || "Could not grant access.");
    }
  };

  const onRevoke = async (id) => {
    setActionError("");
    setActionOk("");
    try {
      await revokeAccess({ variables: { id } });
      setActionOk("Request declined.");
    } catch (err) {
      setActionError(err.message || "Could not revoke access.");
    }
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "name":
        return <ClippedText value={row.name} />;
      case "email":
        return (
          <ClippedText
            value={row.email}
            title={valueAndReason(row.email, row.reason)}
          />
        );
      case "company":
        return (
          <ClippedText
            value={row.company}
            title={valueAndReason(row.company, row.reason)}
          />
        );
      case "resourceName": {
        // One request can cover several documents: list them all.
        const names = documentsOf(row);
        return <ClippedText value={names.join(", ")} />;
      }
      case "reason":
        return <ClippedText value={row.reason} />;
      case "accessState":
        return (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              label={STATE_LABELS[row.accessState] || row.accessState}
              sx={
                row.accessState === "approved"
                  ? {
                      color: theme.customColors.status.success.text,
                      backgroundColor: theme.customColors.status.success.bg,
                    }
                  : row.accessState === "pending"
                    ? {
                        color: theme.customColors.status.warning.text,
                        backgroundColor: theme.customColors.status.warning.bg,
                      }
                    : { backgroundColor: theme.customColors.background.light }
              }
            />
            {row.sharedByAdmin && row.accessState !== "pending" && (
              <Chip
                size="small"
                label="Shared"
                sx={{ backgroundColor: theme.customColors.background.light }}
              />
            )}
          </Stack>
        );
      case "requestedAt":
        return formatDate(row.requestedAt);
      case "actions":
        return (
          // Short labels so both fit; the full action is in the name.
          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="flex-end"
            sx={{ flexWrap: "nowrap" }}
          >
            <Button
              size="small"
              disabled={busy}
              onClick={() => onGrant(row.id)}
              aria-label={`Accept request from ${row.name || row.email}`}
              sx={{
                ...primaryButtonSx(theme),
                ...noWrap,
                minWidth: 0,
                px: 1.5,
                py: 0.25,
              }}
            >
              Accept
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => onRevoke(row.id)}
              aria-label={`Decline request from ${row.name || row.email}`}
              sx={{
                ...outlinedButtonSx(theme),
                ...noWrap,
                minWidth: 0,
                px: 1.5,
                py: 0.25,
                color: theme.customColors.status.error.text,
                borderColor: theme.customColors.status.error.text,
              }}
            >
              Decline
            </Button>
          </Stack>
        );
      default:
        return row[column.field] || "—";
    }
  };

  if (error && !page) {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: "12px", alignItems: "center" }}
        action={
          <RefreshButton
            label="Access requests"
            queries={["getTrustCenterAccessRequests"]}
          />
        }
      >
        Could not load access requests. Click refresh to try again.
      </Alert>
    );
  }

  const subtitle =
    queue === "progress"
      ? pendingCount > 0
        ? `${pendingCount} waiting. Accept emails them the document. Decline sends nothing.`
        : "Accept emails them the document. Decline sends nothing."
      : "Accepted and declined requests.";

  return (
    <>
      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: "12px" }}
          onClose={() => setActionError("")}
        >
          {actionError}
        </Alert>
      )}
      {actionOk && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: "12px" }}
          onClose={() => setActionOk("")}
        >
          {actionOk}
        </Alert>
      )}
      {onSaveAlerts && (
        <RequestAlertsCard emails={alertEmails} onSave={onSaveAlerts} />
      )}
      <ListCard
        title="Access requests"
        titleExtra={
          <ToggleButtonGroup
            exclusive
            size="small"
            value={queue}
            onChange={(_, value) => {
              if (value) setQueue(value);
            }}
            aria-label="Request queue"
            sx={{
              borderRadius: "26px",
              border: `1px solid ${theme.customColors.border.main}`,
              gap: 0.2,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "36px",
                color: theme.customColors.text.secondary,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                px: 2,
                py: 0.75,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": { backgroundColor: theme.palette.primary.dark },
                },
              },
            }}
          >
            <ToggleButton value="progress">In progress</ToggleButton>
            <ToggleButton value="completed">Completed</ToggleButton>
          </ToggleButtonGroup>
        }
        refreshQueries={["getTrustCenterAccessRequests"]}
        subtitle={subtitle}
        searchQuery={search}
        onSearchChange={setSearch}
        onClearFilters={() => setSearch("")}
        countNoun="request"
        columns={queue === "progress" ? progressColumns : completedColumns}
        rows={rows}
        renderCell={renderCell}
        sortBy={sortBy}
        onSortChange={onSortChange}
        emptyText={
          queue === "progress"
            ? "No requests waiting. When a visitor asks for a locked file, it shows up here."
            : "No completed requests yet."
        }
      />
    </>
  );
};

export default AccessRequestsTab;
